/*eslint max-lines: ["error", 600]*/
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { fontMerriweather, fontRoboto } from "@product/scmp-sdk";

import { ArticleHeaderAdSlot } from "~/components/article/article-header-ad-slot";
import { ArticleContentRightSection } from "~/components/article/article-render/article-content/article-content-right-section";
import { pageStyle } from "~/components/article/article-render/article-content/standard-styles";
import {
  StyledAdSlot,
  StyledArticleLiveTag,
  StyledContentHeadline,
} from "~/components/article/article-render/styles";
import { StyledLi, StyledUl } from "~/components/schema-render/content/styles";
import type { WithStickyStackedHeightProps } from "~/lib/styles";
import { setFullContentWidthStyles, setFullViewportWidthStyles, ZIndexLayer } from "~/lib/styles";

import { LiveArticleContent } from "./content";
import { LiveArticleSummary } from "./summary";
import { LiveArticleTimeline } from "./timeline";

export const LiveArticleContainer = styled.div`
  display: grid;

  ${setFullContentWidthStyles()}

  grid:
    "section" min-content
    "headline" min-content
    "sub-headline" min-content
    "sponsor" min-content
    "follow" min-content
    "author" min-content
    "mobile-content-header-ad" min-content
    "leading-image" min-content
    "update-alert" min-content
    "introduction" min-content
    "content" min-content
    "live-subscribe" min-content
    "live-summary" min-content
    "live-content" min-content
    "mobile-footer-newsletter" min-content
    "mobile-content-footer-ad" min-content
    "comment" min-content
    "topic" min-content
    "more-on-this" min-content
    "mobile-footer-ad" min-content
    / 100%;

  ${props => props.theme.breakpoints.up("tablet")} {
    grid:
      "section section section" min-content
      "headline headline headline" min-content
      "sub-headline sub-headline sub-headline" min-content
      "sponsor sponsor sponsor" min-content
      "author author author" min-content
      "leading-image leading-image leading-image" min-content
      "update-alert update-alert update-alert" min-content
      "left . introduction" min-content
      "left . content" min-content
      "left . live-subscribe" min-content
      "left . live-summary" min-content
      "left . live-content" min-content
      "left . mobile-footer-newsletter" min-content
      "left . mobile-content-footer-ad" min-content
      "left . comment" min-content
      "left . topic" min-content
      "left . more-on-this" min-content
      "left . ." min-content
      / 60px 12px minmax(0, 1fr);

    padding-block-end: 42px;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    grid:
      "header-ad header-ad header-ad header-ad header-ad" min-content
      ". . section section section" min-content
      ". . headline headline headline" min-content
      ". . sub-headline sub-headline sub-headline" min-content
      ". . sponsor sponsor sponsor" min-content
      ". . author author author" min-content
      "leading-image leading-image leading-image leading-image leading-image" min-content
      "update-alert update-alert update-alert update-alert update-alert" min-content
      "left . introduction . right" min-content
      "left . content . right" min-content
      "left . live-subscribe . right" min-content
      "left . live-summary . right" min-content
      "left . live-content . right" min-content
      "left . comment . right" min-content
      "left . topic . right" min-content
      "left . . . right" min-content
      / 60px 12px minmax(0, 1fr) 24px 300px;
  }

  ${props => props.theme.breakpoints.up("mediumDesktop")} {
    grid:
      "header-ad header-ad header-ad header-ad header-ad header-ad header-ad" min-content
      ". . . section section section section" min-content
      ". . . headline headline headline headline" min-content
      ". . . sub-headline sub-headline sub-headline sub-headline" min-content
      ". . . sponsor sponsor sponsor sponsor" min-content
      ". . . author author author author" min-content
      "leading-image leading-image leading-image leading-image leading-image leading-image leading-image" min-content
      "update-alert update-alert update-alert update-alert update-alert update-alert update-alert" min-content
      ". left timeline . introduction . right" min-content
      ". left timeline . content . right" min-content
      ". left timeline . live-subscribe . right" min-content
      ". left timeline . live-summary . right" min-content
      ". left timeline . live-content . right" min-content
      ". left timeline . comment . right" min-content
      ". left timeline . topic . right" min-content
      ". left timeline . . . right" min-content
      / 76px 60px calc(20% - 60px) 35px minmax(0, 1fr) 24px 300px;
  }

  > *:empty {
    margin: 0;
    padding: 0;
  }
`;

export const Section = styled.div`
  grid-area: section;

  margin-block-start: 8px;
  padding-inline: 16px;

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 34px;
    padding-inline: 53px;
  }
`;

export const Headline = styled.div`
  grid-area: headline;

  margin-block-start: 8px;
  padding-inline: 16px;

  font-weight: 900;
  font-size: 24px;
  font-family: ${fontMerriweather};
  line-height: 33px;

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 12px;
    padding-inline: 53px;

    font-size: 34px;
    line-height: 46px;
  }

  ${StyledContentHeadline} {
    display: inline;
  }

  ${StyledArticleLiveTag} {
    margin-inline-end: 13px;

    transform: translateY(-2px);

    ${props => props.theme.breakpoints.up("tablet")} {
      transform: translateY(-6px);
    }
  }
`;

export const SubHeadline = styled.div`
  grid-area: sub-headline;

  margin-block-start: 12px;
  padding-inline: 16px;

  font-weight: 300;
  font-size: 19px;
  font-family: ${fontRoboto};
  line-height: 23px;

  ${StyledUl} {
    padding-inline-start: 18px;
  }

  ${StyledLi} + ${StyledLi} {
    margin-block-start: 10px;
  }

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 16px;
    padding-inline: 53px;

    font-size: 24px;
    line-height: 32px;

    ${StyledUl} {
      padding-inline-start: 18px;
    }

    ${StyledLi} + ${StyledLi} {
      margin-block-start: 12px;
    }
  }
`;

export const Sponsor = styled.div`
  grid-area: sponsor;
`;

export const Follow = styled.div`
  grid-area: follow;

  margin-block-start: 18px;

  ${props => props.theme.breakpoints.up("tablet")} {
    display: none;
  }
`;

export const Author = styled.div`
  grid-area: author;

  margin-block-start: 20px;
  padding-inline: 12px;

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 32px;
    padding-inline: 53px;
  }
`;

export const Leading = styled.div`
  grid-area: leading-image;

  margin-block-end: 20px;

  ${props => props.theme.breakpoints.down("tablet")} {
    ${setFullViewportWidthStyles()}
  }

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block: 40px 40px;
  }
`;

type LiveUpdateAlertContainerProps = WithStickyStackedHeightProps & {
  $isShow: boolean;
};

export const LiveUpdateAlertContainer = styled.div<LiveUpdateAlertContainerProps>`
  grid-area: update-alert;

  position: sticky;
  inset-block-start: ${props => props.$stickyStackedHeight + 40}px;
  z-index: ${ZIndexLayer.LiveUpdateAlert};

  display: flex;
  justify-content: center;
  align-items: center;

  inline-size: 100%;
  block-size: 0;

  opacity: 0;

  pointer-events: none;

  transition: opacity 0.1s ease-in-out;

  ${props =>
    props.$isShow
      ? css`
          opacity: 1;
          pointer-events: unset;
        `
      : null}

  ${props => props.theme.breakpoints.up("desktop")} {
    inset-block-start: 100px;
  }
`;

export const LiveUpdateAlert = styled.div`
  padding-block: 12px;
  padding-inline: 20px;

  color: #ffffff;
  font-size: 16px;
  font-family: ${fontRoboto};
  line-height: 19px;

  border-radius: 22.5px;

  background-color: #000000;

  cursor: pointer;

  opacity: 0.95;
`;

export const Introduction = styled.div`
  grid-area: introduction;

  margin-block-end: 14px;

  color: #000000;
  font-weight: 700;
  font-size: 16px;
  font-family: ${fontRoboto};
  line-height: 16px;
  text-transform: uppercase;

  ${props => props.theme.breakpoints.up("desktop")} {
    margin-block-end: 16px;
  }
`;

export const Content = styled.div`
  grid-area: content;

  ${props => pageStyle(props)}
`;

export const LiveSubscribe = styled.div`
  grid-area: live-subscribe;

  margin-block: 12px;
`;

export const StyledLiveArticleContent = styled(LiveArticleContent)`
  grid-area: live-content;
`;

export const StyledLiveArticleSummary = styled(LiveArticleSummary)`
  grid-area: live-summary;
`;

export const TimelineContainer = styled.div<WithStickyStackedHeightProps>`
  grid-area: timeline;

  position: sticky;
  inset-block-start: ${props => props.$stickyStackedHeight + 43}px;

  display: none;
  align-self: flex-start;

  ${props => props.theme.breakpoints.up("mediumDesktop")} {
    display: flex;
    flex: 1;
  }
`;

export const StyledLiveArticleTimeline = styled(LiveArticleTimeline)`
  block-size: 95vh;

  overflow: scroll;

  -ms-overflow-style: none;
  scrollbar-width: none;

  &:after {
    content: "";

    display: block;

    block-size: 40%;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const CommentAndReaction = styled.div`
  grid-area: comment;

  margin-block-start: 52px;

  ${props => props.theme.breakpoints.up("tablet")} {
    max-inline-size: 335px;
  }
`;

export const MoreOnThis = styled.div`
  grid-area: more-on-this;

  margin-block-start: 32px;

  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block: 32px;
    padding-inline: 20px;
  }

  ${props => props.theme.breakpoints.up("desktop")} {
    display: none;
  }
`;

export const Topic = styled.div`
  grid-area: topic;

  margin-block-start: 20px;
  ${props => props.theme.breakpoints.up("tablet")} {
    margin-block-start: 16px;
  }
`;

export const StyledArticleContentRightSection = styled(ArticleContentRightSection)`
  grid-area: right;
`;

export const RightAvailableSpaceTracker = styled.div`
  grid-row: content-start / topic-end;
  grid-column: span 1;

  display: none;

  inline-size: 0;

  ${props => props.theme.breakpoints.up("desktop")} {
    display: flex;
  }
`;

export const NewsletterWidgetContainer = styled.div`
  padding-block: 16px;
  padding-inline: 0;

  border: 1px solid #d8d8d8;
  border-width: 1px 0;
`;

export const MobileFooterNewsletter = styled(NewsletterWidgetContainer)`
  grid-area: mobile-footer-newsletter;

  display: block;

  margin-block-start: 32px;

  ${props => props.theme.breakpoints.up("desktop")} {
    display: none;
  }
`;

export const Left = styled.div<WithStickyStackedHeightProps>`
  grid-area: left;

  position: sticky;
  inset-block-start: ${props => props.$stickyStackedHeight + 43}px;

  display: none;
  align-self: flex-start;

  ${props => props.theme.breakpoints.up("tablet")} {
    display: flex;
    flex-flow: row nowrap;
  }
`;

export const SharePanel = styled.div`
  display: flex;
  flex-basis: 60px;
  flex-flow: column nowrap;
  gap: 60px;
  align-items: flex-start;
`;

export const DesktopHeaderAdSlot = styled(ArticleHeaderAdSlot)`
  grid-area: header-ad;

  display: none;

  background-color: rgba(247, 247, 247, 0.9);

  ${props => props.theme.breakpoints.up("desktop")} {
    display: block;
  }
`;

const MobileAdSlot = styled.div`
  ${setFullViewportWidthStyles()}
  ${props => props.theme.breakpoints.up("tablet")} {
    display: none;
  }
`;

export const MobileContentHeaderAdSlot = styled(MobileAdSlot)`
  grid-area: mobile-content-header-ad;

  margin-block: 20px 18px;

  ${StyledAdSlot} {
    inline-size: fit-content;
    margin: auto;
  }
`;

export const MobileContentFooterAdSlot = styled(MobileAdSlot)`
  grid-area: mobile-content-footer-ad;
`;

export const MobileFooterAdSlot = styled(MobileAdSlot)`
  grid-area: mobile-footer-ad;
`;
